import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProfile } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MapPin, Building2, MessageCircle, Clock, Users, ArrowLeft, FileText } from 'lucide-react';
import { getRateTypeLabel, getWhatsAppLink } from '../lib/utils';
import { toast } from 'sonner';
import { RiderUpload } from '../components/RiderUpload';

export default function VenueDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id === 'me') {
      navigate('/venues/me', { replace: true });
      return;
    }
    loadVenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const loadVenue = async () => {
    try {
      const response = await getProfile(id);
      if (response.data.type !== 'VENUE') {
        navigate('/venues', { replace: true });
        return;
      }
      setVenue(response.data);
    } catch (error) {
      toast.error('Venue not found');
      navigate('/venues', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="content-container">
        <div className="h-64 bg-card animate-pulse rounded-lg"></div>
      </div>
    );
  }

  if (!venue) return null;

  // Use whatsappLink from API (server-side sanitized) or fallback to client-side
  const whatsappLink = venue.whatsappLink || null;

  return (
    <div className="content-container">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-4 transition-colors"
        data-testid="back-btn"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back</span>
      </button>

      <Card className="overflow-hidden bg-card border-border">
        {/* Hero */}
        <div className="h-32 md:h-48 bg-gradient-to-br from-zinc-800 to-zinc-900 relative">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=800')] bg-cover bg-center opacity-30"></div>
        </div>

        <CardContent className="p-4 md:p-6 -mt-16 relative">
          <div className="bg-card rounded-lg p-4 md:p-6 border border-border">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold tracking-tight text-white">
                  {venue.venueName}
                </h1>

                <div className="flex items-center gap-2 text-muted-foreground mt-2">
                  <MapPin className="h-4 w-4" />
                  <span>
                    {venue.city}, {venue.country}
                  </span>
                </div>

                {venue.contactPersonName && (
                  <div className="flex items-center gap-2 text-muted-foreground mt-1">
                    <Users className="h-4 w-4" />
                    <span>Contact: {venue.contactPersonName}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Bio */}
            {venue.shortBio && (
              <div className="mt-6">
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">About</div>
                <p className="text-sm leading-relaxed text-foreground">{venue.shortBio}</p>
              </div>
            )}

            {/* Operational Info */}
            <div className="mt-6 space-y-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Operational Details</div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {venue.defaultArrivalTime && (
                  <div className="p-3 bg-secondary/50 rounded-lg border border-border">
                    <div className="text-xs text-muted-foreground">Arrival Time</div>
                    <div className="font-medium text-primary">{venue.defaultArrivalTime}</div>
                  </div>
                )}
                {venue.rateTypeDefault && (
                  <div className="p-3 bg-secondary/50 rounded-lg border border-border">
                    <div className="text-xs text-muted-foreground">Rate Type</div>
                    <div className="font-medium text-primary">{getRateTypeLabel(venue.rateTypeDefault)}</div>
                  </div>
                )}
                {venue.bookingCoverageHours && (
                  <div className="p-3 bg-secondary/50 rounded-lg border border-border">
                    <div className="text-xs text-muted-foreground">Coverage</div>
                    <div className="font-medium text-primary">{venue.bookingCoverageHours} hours</div>
                  </div>
                )}
                {venue.soundcheckDurationMins && (
                  <div className="p-3 bg-secondary/50 rounded-lg border border-border">
                    <div className="text-xs text-muted-foreground">Soundcheck</div>
                    <div className="font-medium text-primary">{venue.soundcheckDurationMins} mins</div>
                  </div>
                )}
                {venue.responseTimeMins && (
                  <div className="p-3 bg-secondary/50 rounded-lg border border-border">
                    <div className="text-xs text-muted-foreground">Response Time</div>
                    <div className="font-medium text-primary">{venue.responseTimeMins} mins</div>
                  </div>
                )}
              </div>

              {venue.venueProvides?.length > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground mb-2">Venue Provides</div>
                  <div className="flex flex-wrap gap-2">
                    {venue.venueProvides.map((item, i) => (
                      <Badge key={`vp-${item}-${i}`} variant="secondary" className="bg-secondary border-border">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {venue.talentBrings?.length > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground mb-2">Talent Should Bring</div>
                  <div className="flex flex-wrap gap-2">
                    {venue.talentBrings.map((item, i) => (
                      <Badge key={`tb-${item}-${i}`} variant="outline" className="border-border">
                        {item}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {venue.hardStopEnabled && venue.hardStopReason && (
                <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-orange-400">
                    <Clock className="h-4 w-4" />
                    <span className="font-medium">Hard Stop: {venue.hardStopReason}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp CTA */}
      {/* ── Venue Riders ── */}
      <div className="mb-3">
        <div
          className="p-4"
          style={{
            background: 'linear-gradient(180deg, rgba(240, 235, 224, 0.02) 0%, var(--bg-surface) 100%)',
            border: '1px solid var(--border-card)',
            borderRadius: '12px',
            boxShadow: 'var(--card-shadow)',
          }}
          data-testid="riders-card"
        >
          <h2
            className="mb-3"
            style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSynthesis: 'none', letterSpacing: '-0.01em', lineHeight: 1.15, fontSize: '18px', color: 'var(--text-primary)', }}
          >
            VENUE RIDERS
          </h2>
          <RiderUpload profileId={id} editable={false} />
        </div>
      </div>

      {whatsappLink && (
        <div className="mt-6">
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp"
            data-testid="whatsapp-btn"
          >
            <MessageCircle className="h-5 w-5" />
            Message on WhatsApp
          </a>
        </div>
      )}
    </div>
  );
}
